import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Ask a One-Time Donor to Become a Monthly Supporter';
const DESCRIPTION =
    "A practical script and timing guide for converting a one-time gift into recurring monthly support — when to ask, what to say, and why the second ask is easier than the first.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover20.jpg',
            datePublished: '2026-07-29',
            dateModified: '2026-07-29',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How soon after a first gift should I ask someone to go monthly?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Wait until after their first gift has been acknowledged with a genuine thank-you and, ideally, one update showing impact — usually 4-8 weeks. Asking in the same message as the thank-you can feel transactional.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What if a one-time donor says no to going monthly?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Thank them warmly and keep them on your one-time list without pressure. Many people who decline the first ask say yes to a later one, once they've seen more updates and trust the relationship isn't conditional on the answer.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask every one-time donor to switch to monthly?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'No — focus on donors who gave a meaningful amount, opened or replied to your updates, or have given more than once. Asking every single one-time giver, including small or one-off holiday gifts, wastes the ask on people unlikely to convert.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What is a good way to phrase the ask without sounding pushy?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Frame it as an option, not a request: mention that some supporters prefer giving monthly because it is easier to budget and helps you plan, then give a specific, modest suggested amount and a one-click way to say yes.',
                    },
                },
            ],
        },
    ],
};

export default function HowToAskAOneTimeDonorToBecomeAMonthlySupporter() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-one-time-to-monthly-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover20.jpg" alt="A missionary sending a follow-up message to a one-time donor" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>A one-time gift is easy to celebrate and easy to forget about. But the donors who already gave once are your best prospects for recurring support — they've proven they believe in your work, they know your name, and they've already made the harder decision to give at all. Asking them to switch to monthly isn't a second, separate ask; it's a natural next step, if you time it and phrase it right.</p>

                    <h2>Why converting existing donors beats finding new ones</h2>

                    <p>A brand-new monthly donor requires you to build trust from zero. A former one-time donor has already cleared that bar. Converting even a handful of past one-time givers into $25-$50/month supporters can add more predictable income than weeks of cold outreach, because you're not persuading a stranger — you're offering an easier way to keep doing something they already chose to do once.</p>

                    <h2>When to make the ask</h2>

                    <p>Timing matters more than wording. Asking in the same breath as the thank-you for their first gift can feel like a bait-and-switch — as if the thank-you had a string attached. Instead, thank them properly first, send at least one genuine update that shows what their gift helped make possible, and then make the monthly ask 4-8 weeks after the original gift. By then they've seen the impact, not just the appeal.</p>

                    <h2>Who to ask (and who to skip)</h2>

                    <p>Not every one-time donor is a good candidate. Prioritize people who gave a meaningful amount relative to a typical gift, who opened or replied to your updates, or who have given more than once already. Skip small, one-off holiday or emergency-appeal gifts from people you don't otherwise know — asking everyone dilutes the message and can make genuinely warm relationships feel like a mass campaign.</p>

                    <h2>A script that doesn't sound like a pitch</h2>

                    <p>The best version of this ask reads like a helpful suggestion, not a request for more money. Something like: "Thank you again for your gift back in [month] — it meant a lot. A number of our supporters have found it easier to give monthly rather than one-time, since it's simpler to budget for and helps me plan ahead more reliably. If that sounds like something you'd want to try, even $25/month would make a real difference — no pressure either way, and I'm grateful for your support regardless." Notice the ask names a specific, modest amount, gives an easy out, and reframes monthly giving as a convenience to them, not just a benefit to you.</p>

                    <h2>Make the switch as frictionless as possible</h2>

                    <p>Every extra step between "yes" and an active recurring gift loses converts. Send a direct link to your giving page with the monthly option pre-selected if your platform supports it, rather than a general link they have to navigate. The fewer clicks between agreeing and completing the setup, the higher your conversion rate on this ask.</p>

                    <h2>If they say no</h2>

                    <p>A "no" to monthly giving is not a rejection of you — many people simply prefer the flexibility of giving when they choose to. Thank them warmly, keep them on your one-time donor list without any hint of disappointment, and revisit the ask again in 6-12 months after more updates. The same relationship-first approach described in <a href="/resources/donor-retention-for-missionaries">donor retention for missionaries</a> applies here: a donor who never feels pressured is far more likely to say yes eventually than one who felt cornered the first time.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How soon after a first gift should I ask someone to go monthly?</strong><br />
                    Wait 4-8 weeks and send a genuine update first — don't combine it with the thank-you.</p>

                    <p><strong>What if a one-time donor says no to going monthly?</strong><br />
                    Thank them and keep them on your list without pressure; many say yes to a later ask.</p>

                    <p><strong>Should I ask every one-time donor to switch to monthly?</strong><br />
                    No — focus on donors who gave meaningfully or engaged with your updates.</p>

                    <p><strong>What is a good way to phrase the ask without sounding pushy?</strong><br />
                    Frame it as an easier option for them, with a specific modest amount and a clear, low-pressure exit.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries: How to Stop Supporters From Quietly Lapsing</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-thank-you-letter">How to Write a Missionary Thank-You Letter Donors Actually Remember</a></li>
                        <li><a href="/resources/what-to-do-when-a-major-donor-stops-giving">What to Do When a Major Donor Stops Giving</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this platform recurring-first because the missionaries we talked to said monthly support, not one-time gifts, was what let them actually plan their year.</p>
                    <p><small><em>Last updated: 2026-07-29.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
